import React from 'react'

import { F0Dialog, StandardLayout } from '@factorialco/f0-react'
import { TimesheetPageShiftFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { minutesToText } from 'modules/core/lib/duration'
import i18n from 'modules/core/lib/i18n'

import CustomTwoColumnsList from 'modules/attendance/components/CustomTwoColumnsList'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import { TimesheetFeatures } from 'modules/attendance/lib/timesheetFeatures'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'
import { calculateTimesheetBalanceBreakdown } from 'modules/attendance/lib/utils/timesheetBalanceBreakdownCalculations'
import { AggregateWorkedTimeBlocksFilter } from 'modules/attendance/store/models/AggregateWorkedTimeFilter'
import { useFetchTimeSettingsCustomTimeRangeCategories } from 'modules/time_categories_settings/hooks/useFetchTimeSettingsCustomTimeRangeCategories'

type Props = {
  onClose: () => void
  shifts: TimesheetPageShiftFragment[]
}

const TimesheetBalanceSidepanel = ({ onClose, shifts }: Props) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const {
    aggregatedWorkedTime,
    estimatedTimes,
    currentEmployeesSetting,
    balanceMinutes,
    extraHoursMinutes,
    complementaryHoursMinutes,
    isPartTime,
    featureByName,
  } = useTimesheetData()
  const { categories: timeSettingsCategories } =
    useFetchTimeSettingsCustomTimeRangeCategories(companyId)
  const extraHoursBaseline = currentEmployeesSetting?.extraHoursBaseline
  const complementaryHoursBaseline = currentEmployeesSetting?.complementaryHoursBaseline
  const hasWorkingLocation = currentEmployeesSetting?.enableClockInLocation ?? false
  const hasComplementaryHoursFeature = TimesheetFeatures.COMPLEMENTARY_HOURS in featureByName
  const showComplementaryHours =
    hasComplementaryHoursFeature && isPartTime && currentEmployeesSetting?.enableComplementaryHours
  const {
    paidBreaksMinutes,
    paidAbsencesMinutes,
    effectiveWorkedMinutes,
    estimatedMinutes,
    extraTimeMinutes,
    trackedMinutes,
    unapprovedOvertimeMinutes,
    trackedMinutesDelta,
    workedMinutes,
    contractMinutes,
    balancedRegularMinutes,
  } = calculateTimesheetBalanceBreakdown({
    aggregatedWorkedTime,
    estimatedTimes,
    extraHoursMinutes,
    complementaryHoursMinutes: showComplementaryHours ? complementaryHoursMinutes : undefined,
  })

  const extraTimeMinutesText =
    extraTimeMinutes === undefined ? '-' : minutesToText(extraTimeMinutes, true)

  const hoursTypeLabel = showComplementaryHours
    ? i18n.t('time_tracking.timesheet.sidepanel.complementary_hours')
    : i18n.t('time_tracking.timesheet.sidepanel.extra_hours')

  const hoursTypeTooltipLabel = showComplementaryHours
    ? i18n.t('time_tracking.timesheet.sidepanel.tooltips.complementary_hours.label')
    : i18n.t('time_tracking.timesheet.sidepanel.tooltips.extra_hours.label')

  const hoursTypeTooltipDescription = showComplementaryHours
    ? i18n.t('time_tracking.timesheet.sidepanel.tooltips.complementary_hours.description')
    : i18n.t('time_tracking.timesheet.sidepanel.tooltips.extra_hours.description')

  const locationTypesWithPercentage = AttendanceShiftUtils.totalMinutesPerLocationPercentage(shifts)
  const workingLocationItems = Object.entries(locationTypesWithPercentage).map(([title, info]) => ({
    title,
    info,
  }))
  const showWorkingLocation = workingLocationItems.length > 0 && hasWorkingLocation

  // Calculate special hours (time categories) breakdown
  const workedTimeBlocks = aggregatedWorkedTime?.workedTimeBlocks ?? []
  const filter = new AggregateWorkedTimeBlocksFilter(workedTimeBlocks)
  const specialHoursItems =
    timeSettingsCategories.hasTimeSettingsCategories &&
    filter.withApprovedTimeSettingsCategoryMinutes
      ? Object.entries(filter.minutesByCategory)
          .map(([categoryId, totalMinutes]) => {
            const category = timeSettingsCategories.customTimeRangeCategories.find(
              (cat) => cat.id === +categoryId
            )
            if (!category) return null
            return { title: category.name, info: minutesToText(totalMinutes, true) }
          })
          .filter((item): item is { title: string; info: string } => item !== null)
      : []

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      position="right"
      title={i18n.t('time_tracking.timesheet.sidepanel.title')}
    >
      <StandardLayout data-testid="balance-breakdown-content">
        <CustomTwoColumnsList
          data-testid="breakdown-section-balance"
          title={i18n.t('time_tracking.timesheet.sidepanel.total_balance')}
          titleValue={minutesToText(balanceMinutes, true, true)}
          titleTooltip={{
            label: i18n.t('time_tracking.timesheet.sidepanel.tooltips.balance.label'),
            description: i18n.t('time_tracking.timesheet.sidepanel.tooltips.balance.description'),
          }}
          list={[
            {
              title: i18n.t('time_tracking.timesheet.sidepanel.planned_time'),
              info: minutesToText(estimatedMinutes, true),
            },
            {
              title: i18n.t('time_tracking.timesheet.sidepanel.worked_time'),
              info: minutesToText(workedMinutes, true),
            },
          ]}
        />
        <CustomTwoColumnsList
          data-testid="breakdown-section-worked-time"
          title={i18n.t('time_tracking.timesheet.sidepanel.worked_time')}
          titleValue={minutesToText(workedMinutes, true)}
          titleTooltip={{
            label: i18n.t('time_tracking.timesheet.sidepanel.tooltips.worked_time.label'),
            description: i18n.t(
              'time_tracking.timesheet.sidepanel.tooltips.worked_time.description'
            ),
          }}
          list={[
            {
              title: i18n.t('time_tracking.timesheet.sidepanel.regular_hours'),
              info: minutesToText(balancedRegularMinutes, true),
            },
            {
              title: i18n.t('time_tracking.timesheet.sidepanel.paid_breaks'),
              info: minutesToText(paidBreaksMinutes, true),
            },
            {
              title: hoursTypeLabel,
              info: extraTimeMinutesText,
            },
          ]}
        />
        <CustomTwoColumnsList
          data-testid="breakdown-section-effective-worked-time"
          title={i18n.t('time_tracking.timesheet.sidepanel.effective_worked_time')}
          titleValue={minutesToText(effectiveWorkedMinutes, true)}
          titleTooltip={{
            label: i18n.t('time_tracking.timesheet.sidepanel.tooltips.effective_worked_time.label'),
            description: i18n.t(
              'time_tracking.timesheet.sidepanel.tooltips.effective_worked_time.description'
            ),
          }}
          list={[
            {
              title: i18n.t('time_tracking.timesheet.sidepanel.paid_absences'),
              info: minutesToText(paidAbsencesMinutes, true),
            },
            {
              title: i18n.t('time_tracking.timesheet.sidepanel.worked_time'),
              info: minutesToText(workedMinutes, true),
            },
          ]}
        />
        <CustomTwoColumnsList
          data-testid="breakdown-section-extra-hours"
          title={hoursTypeLabel}
          titleValue={extraTimeMinutesText}
          titleTooltip={{
            label: hoursTypeTooltipLabel,
            description: hoursTypeTooltipDescription,
          }}
          list={[
            ...((showComplementaryHours ? complementaryHoursBaseline : extraHoursBaseline) ===
            'contract_hours'
              ? [
                  {
                    title: i18n.t('time_tracking.timesheet.sidepanel.contract_hours'),
                    info: minutesToText(contractMinutes, true),
                  },
                ]
              : [
                  {
                    title: i18n.t('time_tracking.timesheet.sidepanel.planned_hours'),
                    info: minutesToText(estimatedMinutes, true),
                  },
                ]),
            ...((showComplementaryHours ? complementaryHoursBaseline : extraHoursBaseline) ===
            'contract_hours'
              ? [
                  {
                    title: i18n.t('time_tracking.timesheet.sidepanel.effective_worked_time'),
                    info: minutesToText(effectiveWorkedMinutes, true),
                  },
                ]
              : [
                  {
                    title: i18n.t('time_tracking.timesheet.sidepanel.worked_time'),
                    info: minutesToText(workedMinutes, true),
                  },
                ]),
          ]}
        />
        <CustomTwoColumnsList
          data-testid="breakdown-section-tracked-time"
          title={i18n.t('time_tracking.timesheet.sidepanel.tracked_time')}
          titleValue={minutesToText(trackedMinutes, true)}
          titleTooltip={{
            label: i18n.t('time_tracking.timesheet.sidepanel.tooltips.tracked_time.label'),
            description: i18n.t(
              'time_tracking.timesheet.sidepanel.tooltips.tracked_time.description'
            ),
          }}
          list={[
            {
              title: i18n.t('time_tracking.timesheet.sidepanel.unapproved_overtime'),
              info:
                unapprovedOvertimeMinutes > 0
                  ? `-${minutesToText(unapprovedOvertimeMinutes, true)}`
                  : minutesToText(0, true),
            },
            {
              title: i18n.t('time_tracking.timesheet.sidepanel.adjusted_time_tracked'),
              info:
                trackedMinutesDelta !== 0
                  ? minutesToText(trackedMinutesDelta, true, true)
                  : minutesToText(0, true),
            },
          ]}
        />
        {showWorkingLocation && (
          <CustomTwoColumnsList
            data-testid="breakdown-section-working-location"
            title={i18n.t('time_tracking.timesheet.sidepanel.working_location')}
            list={workingLocationItems}
            titleTooltip={{
              label: i18n.t('time_tracking.timesheet.sidepanel.tooltips.working_location.label'),
              description: i18n.t(
                'time_tracking.timesheet.sidepanel.tooltips.working_location.description'
              ),
            }}
          />
        )}
        {specialHoursItems.length > 0 && (
          <CustomTwoColumnsList
            data-testid="breakdown-section-special-hours"
            title={i18n.t('clock_in.sidepanel.worked_hours.special_hours.title')}
            list={specialHoursItems}
          />
        )}
      </StandardLayout>
    </F0Dialog>
  )
}

export default TimesheetBalanceSidepanel
